import { useState, useEffect } from 'react';
import { Database, RefreshCw, CheckCircle, XCircle, Clock, AlertCircle, Loader, Link as LinkIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

/**
 * Page de test des connexions API et synchronisation RNCP-ROME
 */
export default function ApiConnectionsSettings() {
  const [loading, setLoading] = useState({});
  const [testResults, setTestResults] = useState({});
  const [rncpRomeInfo, setRncpRomeInfo] = useState(null);
  const [syncingRncp, setSyncingRncp] = useState(false);

  // Charger les informations de synchronisation RNCP-ROME au montage
  useEffect(() => {
    loadRncpRomeInfo();
  }, []);

  // Charger les infos de la dernière synchro RNCP-ROME
  const loadRncpRomeInfo = async () => {
    try {
      const response = await api.get('/rncp-rome/info');
      setRncpRomeInfo(response.data);
    } catch (error) {
      console.error('Erreur chargement info RNCP-ROME:', error);
    }
  };

  // Tester une connexion API
  const testConnection = async (apiName, endpoint) => {
    setLoading(prev => ({ ...prev, [apiName]: true }));
    setTestResults(prev => ({ ...prev, [apiName]: null }));

    try {
      const response = await api.get(endpoint);

      setTestResults(prev => ({
        ...prev,
        [apiName]: {
          success: true,
          message: response.data.message || 'Connexion réussie',
          data: response.data,
          timestamp: new Date()
        }
      }));

      toast.success(`✅ ${apiName} : Connexion réussie`);
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        [apiName]: {
          success: false,
          message: error.response?.data?.message || error.message,
          timestamp: new Date()
        }
      }));

      toast.error(`❌ ${apiName} : ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(prev => ({ ...prev, [apiName]: false }));
    }
  };

  // Synchroniser RNCP-ROME
  const syncRncpRome = async () => {
    setSyncingRncp(true);

    try {
      // 1. Télécharger les fichiers
      toast.loading('📥 Téléchargement des fichiers RNCP...', { id: 'sync-rncp' });
      const downloadResponse = await api.post('/import-export/download-rncp', {
        autoImport: false
      });

      if (!downloadResponse.data.success) {
        throw new Error(downloadResponse.data.message);
      }

      // 2. Importer le mapping RNCP-ROME
      toast.loading('💾 Import du mapping RNCP-ROME...', { id: 'sync-rncp' });
      const importResponse = await api.post('/rncp-rome/sync');

      if (importResponse.data.success) {
        toast.success(`✅ Synchronisation réussie : ${importResponse.data.recordsImported} correspondances`, { id: 'sync-rncp' });
        await loadRncpRomeInfo();
      } else {
        throw new Error(importResponse.data.message);
      }
    } catch (error) {
      console.error('Erreur sync RNCP-ROME:', error);
      toast.error(`❌ Erreur synchronisation : ${error.response?.data?.message || error.message}`, { id: 'sync-rncp' });
    } finally {
      setSyncingRncp(false);
    }
  };

  // Tester toutes les connexions
  const testAllConnections = async () => {
    await Promise.all([
      testConnection('Kit Apprentissage', '/kit-apprentissage/search/intitule?q=diplome&limit=1'),
      testConnection('France Travail OAuth2', '/france-travail/test'),
      testConnection('Database', '/health')
    ]);
  };

  // Liste des APIs à tester
  const apiConnections = [
    {
      name: 'Kit Apprentissage',
      description: 'Certif Info - Données certifications',
      endpoint: '/kit-apprentissage/search/intitule?q=diplome&limit=1',
      icon: Database,
      color: 'blue'
    },
    {
      name: 'France Travail OAuth2',
      description: 'API ROME - France Travail',
      endpoint: '/france-travail/test-connection',
      icon: LinkIcon,
      color: 'green'
    },
    {
      name: 'Database',
      description: 'Connexion PostgreSQL',
      endpoint: '/health',
      icon: Database,
      color: 'purple'
    }
  ];

  // Formater la date
  const formatDate = (date) => {
    if (!date) return 'Jamais';
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Tests de Connexion API</h2>
        <p className="text-gray-600 mt-1">
          Vérifiez l'état des connexions aux différentes APIs et services externes
        </p>
      </div>

      {/* Bouton Test All */}
      <div className="flex justify-end">
        <button
          onClick={testAllConnections}
          disabled={Object.values(loading).some(l => l)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <RefreshCw className="w-4 h-4" />
          Tester toutes les connexions
        </button>
      </div>

      {/* Tests de connexion API */}
      <div className="grid grid-cols-1 gap-4">
        {apiConnections.map((api) => {
          const result = testResults[api.name];
          const isLoading = loading[api.name];

          return (
            <div key={api.name} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <div className={`p-3 rounded-lg bg-${api.color}-100`}>
                    <api.icon className={`w-6 h-6 text-${api.color}-600`} />
                  </div>

                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{api.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{api.description}</p>

                    {/* Résultat du test */}
                    {result && (
                      <div className={`mt-3 p-3 rounded-lg flex items-start gap-2 ${
                        result.success
                          ? 'bg-green-50 border border-green-200'
                          : 'bg-red-50 border border-red-200'
                      }`}>
                        {result.success ? (
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <div className={`font-medium ${
                            result.success ? 'text-green-900' : 'text-red-900'
                          }`}>
                            {result.success ? 'Connexion établie' : 'Connexion échouée'}
                          </div>
                          <div className={`text-sm mt-1 ${
                            result.success ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {result.message}
                          </div>
                          {result.data?._simulation && (
                            <div className="text-xs mt-2 text-orange-700 bg-orange-50 border border-orange-200 rounded p-2">
                              ⚠️ Mode simulation activé - Configurez le token dans .env pour l'API réelle
                            </div>
                          )}
                          <div className="text-xs text-gray-500 mt-2">
                            Testé le {formatDate(result.timestamp)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bouton Test */}
                <button
                  onClick={() => testConnection(api.name, api.endpoint)}
                  disabled={isLoading}
                  className="ml-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Test...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Tester
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Synchronisation RNCP-ROME */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-indigo-100">
              <Database className="w-6 h-6 text-indigo-600" />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900">Mapping RNCP ↔ ROME</h3>
              <p className="text-sm text-gray-600 mt-1">
                Correspondances entre certifications RNCP et métiers ROME
              </p>
            </div>
          </div>
        </div>

        {/* Informations de synchronisation */}
        {rncpRomeInfo && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-gray-600">Dernière synchro</div>
                <div className="text-sm font-semibold text-gray-900 mt-1">
                  {formatDate(rncpRomeInfo.lastSync)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Correspondances</div>
                <div className="text-sm font-semibold text-gray-900 mt-1">
                  {rncpRomeInfo.totalRecords?.toLocaleString('fr-FR') || '0'}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Codes RNCP</div>
                <div className="text-sm font-semibold text-gray-900 mt-1">
                  {rncpRomeInfo.uniqueRncp?.toLocaleString('fr-FR') || '0'}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Codes ROME</div>
                <div className="text-sm font-semibold text-gray-900 mt-1">
                  {rncpRomeInfo.uniqueRome?.toLocaleString('fr-FR') || '0'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bouton de synchronisation */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Télécharge et importe les dernières correspondances RNCP-ROME depuis data.gouv.fr
          </div>
          <button
            onClick={syncRncpRome}
            disabled={syncingRncp}
            className="ml-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2 whitespace-nowrap"
          >
            {syncingRncp ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Synchronisation...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Synchroniser
              </>
            )}
          </button>
        </div>

        {/* Avertissement */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <div className="font-medium">Note importante</div>
            <div className="mt-1">
              La synchronisation télécharge ~8Mo de données et peut prendre quelques minutes.
              Les anciennes données seront remplacées par les nouvelles.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
